import type { I18nStoreListener, MissingKeyEntry } from "./i18n-store.types";

export type { MissingKeyEntry } from "./i18n-store.types";

const missing = new Map<string, MissingKeyEntry>();
const listeners = new Set<I18nStoreListener>();
let hooked = false;

// useSyncExternalStore compares snapshots with Object.is(). If getSnapshot()
// creates a new array on every call (Array.from + sort), React sees a new
// reference each render and loops infinitely. We cache the sorted snapshot
// and only recompute it when data actually changes (on notify).
let snapshot: MissingKeyEntry[] = [];

let notifyScheduled = false;

function notify() {
  snapshot = Array.from(missing.values()).sort(
    (a, b) => b.firstSeen - a.firstSeen,
  );
  if (typeof window !== "undefined" && !notifyScheduled) {
    notifyScheduled = true;
    queueMicrotask(() => {
      notifyScheduled = false;
      listeners.forEach((l) => l());
    });
  } else if (typeof window === "undefined") {
    listeners.forEach((l) => l());
  }
}

const SERVER_SNAPSHOT_MISSING_KEYS: MissingKeyEntry[] = [];

export function subscribeI18nStore(listener: I18nStoreListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getMissingKeys(): MissingKeyEntry[] {
  return snapshot;
}

export function getServerSnapshotMissingKeys(): MissingKeyEntry[] {
  return SERVER_SNAPSHOT_MISSING_KEYS;
}

let highlightMissing = false;
let highlightObserver: MutationObserver | null = null;

export function isHighlightMissingActive(): boolean {
  return highlightMissing;
}

export function getServerSnapshotHighlightMissing(): boolean {
  return false;
}

export function refreshDomMissingHighlights(): void {
  if (typeof document === "undefined" || !highlightMissing) return;
  const missingKeys = getMissingKeys();
  if (missingKeys.length === 0) return;

  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const parent = node.parentElement;
    if (!parent || parent.closest("[data-feedback-capture-exclude]")) continue;
    const text = node.nodeValue ?? "";
    if (
      text.includes("EKSİK:") ||
      text.includes("[EKSİK:") ||
      missingKeys.some((k) => text.includes(k.key))
    ) {
      parent.classList.add("i18n-missing-term-highlight");
      parent.setAttribute("data-i18n-missing", "true");
    }
  }
}

export function setHighlightMissing(active: boolean): void {
  highlightMissing = active;
  if (typeof document !== "undefined") {
    let styleEl = document.getElementById("i18n-missing-highlight-style");
    if (active) {
      if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.id = "i18n-missing-highlight-style";
        styleEl.textContent = `
          [data-i18n-highlight-missing="true"] .i18n-missing-term-highlight,
          [data-i18n-highlight-missing="true"] [data-i18n-missing="true"] {
            outline: 2px dashed #ef4444 !important;
            outline-offset: 3px !important;
            background-color: rgba(239, 68, 68, 0.12) !important;
            box-shadow: 0 0 10px rgba(239, 68, 68, 0.3) !important;
            border-radius: 4px !important;
            font-family: inherit !important;
          }
        `;
        document.head.appendChild(styleEl);
      }
      document.body.setAttribute("data-i18n-highlight-missing", "true");
      refreshDomMissingHighlights();

      if (!highlightObserver && typeof MutationObserver !== "undefined") {
        highlightObserver = new MutationObserver(() => {
          refreshDomMissingHighlights();
        });
        highlightObserver.observe(document.body, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
    } else {
      styleEl?.remove();
      document.body.removeAttribute("data-i18n-highlight-missing");
      if (highlightObserver) {
        highlightObserver.disconnect();
        highlightObserver = null;
      }
      document
        .querySelectorAll(
          '.i18n-missing-term-highlight, [data-i18n-missing="true"]',
        )
        .forEach((el) => {
          el.classList.remove("i18n-missing-term-highlight");
          el.removeAttribute("data-i18n-missing");
        });
    }
  }
  notify();
}

export function toggleHighlightMissing(): void {
  setHighlightMissing(!highlightMissing);
}

export function clearMissingKeys(): void {
  missing.clear();
  notify();
}

export function recordMissingTranslation(
  lng: string,
  ns: string,
  key: string,
): void {
  const mapKey = `${lng}:${ns}:${key}`;
  const existing = missing.get(mapKey);
  if (existing) {
    missing.set(mapKey, { ...existing, count: existing.count + 1 });
  } else {
    missing.set(mapKey, { lng, ns, key, count: 1, firstSeen: Date.now() });
  }
  notify();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- i18next instance type not exported
export function initI18nMissingKeyTracker(i18n: any): void {
  if (hooked || !i18n || !i18n.options) return;
  hooked = true;

  // i18next only calls missingKeyHandler (and emits 'missingKey') when
  // options.saveMissing is truthy. Since the app sets saveMissing: false,
  // we enable it here — this function is only called inside DebugPanel
  // which is dev-only (process.env.NODE_ENV === 'development').
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- i18next dynamic
  i18n.options.saveMissing = true;

  // Wrap the existing missingKeyHandler so we record to the store AND
  // preserve the original handler (console logger, etc.)
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- i18next dynamic
  const originalHandler: unknown = i18n.options.missingKeyHandler;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- i18next dynamic
  i18n.options.missingKeyHandler = (
    lng: string | string[],
    ns: string,
    key: string,
    ...rest: unknown[]
  ) => {
    const lngStr = Array.isArray(lng) ? (lng[0] ?? "unknown") : String(lng);
    recordMissingTranslation(lngStr, ns, key);
    if (typeof originalHandler === "function") {
      (originalHandler as (...args: unknown[]) => void)(lng, ns, key, ...rest);
    }
  };
}
